import { CONSTANT } from '@/constants'
import { currentIntlTag } from '@/libs/i18n'
import { addRequestedEmployees, rejectRequestedEmployees } from '@/apiManager/addEmployee'
import { approveChallengeJoinRequest, declineChallengeJoinRequest } from '@/apiManager/gamification'
import { updateGroupParticipants } from '@/apiManager/groups'

// Every per-type behaviour of the notification centre lives here, not in the
// components (CORPORATE_ADMIN_NOTIFICATIONS_PLAN.md §12.7). Deep links are
// paths: several route names are shared, so pushing by name lands wrong.

// Categories whose notifications are live. Each delivery phase adds its own
// (CORPORATE_ADMIN_NOTIFICATIONS_PHASES.md), so admins never see empty tabs
// or settings for a module that has no notifications yet.
export const LIVE_CATEGORIES = ['member', 'challenge', 'group', 'activity', 'package']

export const isLiveCategory = key => LIVE_CATEGORIES.includes(key)

// Tab order on the Notifications page; only the live ones are shown.
export const CATEGORIES = ['member', 'challenge', 'group', 'activity', 'package', 'team'].filter(isLiveCategory)

// Members are the brand's own category, so they wear the accent: inline
// styles never pass through the build's colour rewrite, hence the variables
// (src/theme/presets.js). The other categories keep fixed colours.
const CATEGORY_STYLE = {
  member: { icon: 'UserPlusIcon', color: 'var(--primary-default-fg, #690FAD)', bg: 'var(--cd-shade-f5edfb, #F5EDFB)' },
  challenge: { icon: 'AwardIcon', color: '#16A34A', bg: '#E9F8EF' },
  group: { icon: 'UsersIcon', color: '#2563EB', bg: '#EAF1FE' },
  activity: { icon: 'CalendarIcon', color: '#DC2626', bg: '#FDECEC' },
  package: { icon: 'PackageIcon', color: '#C026D3', bg: '#FAE8FF' },
  team: { icon: 'ShieldIcon', color: '#B45309', bg: '#FEF3E2' },
  billing: { icon: 'CreditCardIcon', color: '#0E7490', bg: '#E0F4F8' },
}
const LEFT_STYLE = { icon: 'UserMinusIcon', color: '#6B7280', bg: '#F1F2F5' }

// The one person in a notification, if it is about exactly one.
export const singlePersonName = item => (item?.count === 1 && item.actors?.[0]?.name) || ''

// `?search=<first name>` for a notification about one person, so the list
// opens searched to them. Only the first name: the lists match first name,
// last name and username separately, so a full name finds nobody.
const personQuery = item => {
  const firstName = singlePersonName(item).trim().split(/\s+/)[0]
  return firstName ? `?search=${encodeURIComponent(firstName)}` : ''
}

const challengePath = (entity, hash = '', item) => (entity?.slug
  ? `/gamification/${entity.slug}${personQuery(item)}${hash}`
  : '/gamification')

// Group pages are keyed by the base64 of the group id.
const groupPath = (entity, hash = '', item) => (entity?.id
  ? `/groups/${btoa(String(entity.id))}${personQuery(item)}${hash}`
  : '/groups')

const decideGroupRequest = status => ({ userId, entity }) => updateGroupParticipants({
  id: entity.id,
  participantIds: [Number(userId)],
  status,
})

// A class's attendees page, keyed by the schedule_detail id; `#view_waitinglist`
// opens its waitlist tab. The page shows the activity name from the query.
export const attendeesPath = (entity, hash = '') => (entity?.id
  ? `/activity-attendees/${entity.id}?activity_name=${encodeURIComponent(entity.title || '')}${hash}`
  : '/activities')

// A day's activity row about one activity (`item.activity`, sent by the server)
// opens that activity's attendees page; otherwise, or once it is deleted /
// cancelled (no page), the Activities list.
const oneActivityPath = (item, hash = '') => (item?.activity?.id && !item.activity.gone
  ? attendeesPath(item.activity, hash)
  : '/activities')

// One package: Packages opens its details (`?open=<package id>`); deleted, or
// no single package, the Packages list. Hosts with a package pop-up show it in
// place instead (`packageToOpen`).
const onePackagePath = pkg => (pkg?.id && !pkg.gone ? `/packages?open=${encodeURIComponent(pkg.id)}` : '/packages')

// There is no page per member: one person opens the list searched to them.
const membersPath = (item, hash = '') => `/employee${personQuery(item)}${hash}`

const TYPES = {
  MEMBER_JOIN_REQUEST: {
    link: (entity, item) => membersPath(item, '#requested_members'),
    request: {
      approve: ({ userId }) => addRequestedEmployees({ userId, status: true }),
      reject: ({ userId }) => rejectRequestedEmployees({ userId, status: false }),
    },
  },
  MEMBER_JOINED: { link: (entity, item) => membersPath(item) },
  MEMBER_LEFT: { left: true, link: () => '/employee' },
  // Removed by an admin: shown like "left" (grey, opens the people pop-up).
  MEMBER_REMOVED: { left: true, verb: 'removed', link: () => '/employee' },
  CHALLENGE_JOIN_REQUEST: {
    link: (entity, item) => challengePath(entity, '#join-request', item),
    request: {
      approve: ({ userId, entity }) => approveChallengeJoinRequest({ userId, challengeSlug: entity.slug }),
      reject: ({ userId, entity }) => declineChallengeJoinRequest({ userId, challengeSlug: entity.slug }),
    },
  },
  CHALLENGE_JOINED: { link: (entity, item) => challengePath(entity, '#participants', item) },
  CHALLENGE_LEFT: { left: true, link: entity => challengePath(entity, '#participants') },
  GROUP_JOIN_REQUEST: {
    link: (entity, item) => groupPath(entity, '#join-request', item),
    request: {
      approve: decideGroupRequest(CONSTANT.GROUP_REQUEST_STATUS.ACCEPTED),
      reject: decideGroupRequest(CONSTANT.GROUP_REQUEST_STATUS.REJECTED),
    },
  },
  GROUP_JOINED: { link: (entity, item) => groupPath(entity, '#group-participants', item) },
  GROUP_LEFT: { left: true, link: entity => groupPath(entity, '#group-participants') },
  // Activities: one row per type per community per day (anti-spam), each
  // person tagged with their activity. About one activity (`item.activity`),
  // a click opens that activity (`opensActivity`); about several, or one
  // since deleted / cancelled, the pop-up (`popup`) shows who did what.
  // `verb` is each person's line in the pop-up ("Booked 6:02 pm").
  // `state` is what the row says about each person; the pop-up shows their
  // live status only when it is no longer that (e.g. "Cancelled 31 Oct").
  ACTIVITY_BOOKED: { popup: true, opensActivity: true, verb: 'booked', state: 'booked', link: (entity, item) => oneActivityPath(item) },
  // Cancelled bookings are no longer on the attendees page: shown like "left".
  ACTIVITY_BOOKING_CANCELLED: {
    left: true, opensActivity: true, verb: 'cancelled', state: 'cancelled', link: (entity, item) => oneActivityPath(item),
  },
  ACTIVITY_WAITLIST_JOINED: {
    popup: true, opensActivity: true, verb: 'waiting', state: 'waiting', link: (entity, item) => oneActivityPath(item, '#view_waitinglist'),
  },
  ACTIVITY_WAITLIST_PROMOTED: { popup: true, opensActivity: true, verb: 'moved', state: 'booked', link: (entity, item) => oneActivityPath(item) },
  ACTIVITY_CHECKED_IN: { popup: true, opensActivity: true, verb: 'checkedIn', state: 'checkedIn', link: (entity, item) => oneActivityPath(item) },
  // About one activity: its attendees page. With people waiting, "Add places"
  // raises its capacity by that many and opens the waitlist to move them in.
  ACTIVITY_CLASS_FULL: { link: entity => attendeesPath(entity), addPlaces: true },
  // Attendance: about one activity, a click opens it; otherwise the pop-up, a
  // line per activity that ended (who came, who didn't), each opening its page.
  ACTIVITY_ENDED: { popup: true, opensActivity: true, attendance: true, link: (entity, item) => oneActivityPath(item) },
  // Packages bought (card or code): one row per day. All purchases of one
  // package (`item.package`): a click opens it; several: the pop-up, a line
  // per purchase with its package and what was paid.
  PACKAGE_PURCHASED: { popup: true, opensPackage: true, verb: 'bought', link: (entity, item) => onePackagePath(item?.package) },
  // A package the community created stops selling tomorrow: it opens, to
  // extend it (the row clears itself once its date changes).
  PACKAGE_EXPIRING: { link: entity => onePackagePath(entity) },
}

export const typeConfig = type => TYPES[type] || { link: () => '/notifications' }

// The one package a click shows in the details pop-up, if any: a "Package
// expiring" row, or "Packages bought" where every purchase is one package.
export const packageToOpen = item => {
  if (item.type === 'PACKAGE_EXPIRING') return item.entity?.id || null
  if (typeConfig(item.type).opensPackage && item.package?.id && !item.package.gone) return item.package.id
  return null
}

export const styleFor = item => (typeConfig(item.type).left ? LEFT_STYLE : CATEGORY_STYLE[item.category] || CATEGORY_STYLE.member)

export const isClosed = item => item.entityStatus && item.entityStatus !== 'active'

/** People who left, and deleted/cancelled things, have no page to open. */
// Deleted or cancelled: the page is gone. A deactivated group's page does not
// load either (its members API answers 404). An ended challenge keeps its page.
export const isGone = item => ['deleted', 'cancelled', 'inactive'].includes(item.entityStatus)

// A request with everyone approved / rejected: its requests page is empty.
export const isHandledRequest = item => !!typeConfig(item.type).request
  && !!item.requiresAction
  && (item.actionStatus === 'resolved' || !(item.pendingCount > 0))

// A day's activity row about one activity, or a "Packages bought" row about
// one package, that still has a page opens it.
const opensOne = item => (!!typeConfig(item.type).opensActivity && !!item.activity && !item.activity.gone)
  || (!!typeConfig(item.type).opensPackage && !!item.package && !item.package.gone)

// These open the people pop-up (who, and how it ended) instead of a page:
// people who left, things gone, handled requests, and a day's activity or
// package row (who did what is only in the pop-up) unless it opens its one
// activity or package.
export const rowOpensPopup = item => !opensOne(item) && (
  !!typeConfig(item.type).left
  || !!typeConfig(item.type).popup
  || isGone(item)
  || isHandledRequest(item))

// The chip on a closed row: "Challenge ended", "Group deactivated", ...
export const closedStatusKey = item => (['cancelled', 'ended', 'inactive'].includes(item.entityStatus)
  ? item.entityStatus
  : 'deleted')

export const canRequestAction = item => !!typeConfig(item.type).request && !isClosed(item)

// A full activity people are waiting for, still running: "5 waiting · Add places".
export const canAddPlaces = item => !!typeConfig(item.type).addPlaces
  && !isClosed(item)
  && item.capacity?.waiting > 0
  && item.capacity?.slots > 0

/**
 * Splits a sentence into plain and bold parts so people's names and the
 * entity title read in bold, as in the design — without v-html.
 */
export const emphasise = (text, terms) => {
  const wanted = [...new Set((terms || []).filter(term => term && term.length > 1))]
    .sort((a, b) => b.length - a.length)
  if (!text || !wanted.length) return [{ text: text || '', bold: false }]
  const escaped = wanted.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const pattern = new RegExp(`(${escaped.join('|')})`, 'g')
  return text.split(pattern).filter(Boolean).map(part => ({ text: part, bold: wanted.includes(part) }))
}

/** Avatars come back as stored: a full URL, or a media path the CDN prefixes. */
export const avatarUrl = path => {
  if (!path) return require('@/assets/images/avatars/default_profile_img.png')
  return /^https?:\/\//.test(path) ? path : CONSTANT.GET_IMAGE_URL(path)
}

// A community with no timezone set reads as Dubai, as on the server.
export const DEFAULT_COMMUNITY_TIMEZONE = 'Asia/Dubai'

/**
 * A date or time in the community's timezone (sent with the bell count, the
 * list and the pop-up), so every admin sees the community's clock, never the
 * browser's. Not loaded yet or unknown: Dubai, as on the server.
 */
export const formatInZone = (value, options, timeZone) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  try {
    return date.toLocaleString(currentIntlTag(), { ...options, timeZone: timeZone || DEFAULT_COMMUNITY_TIMEZONE })
  } catch (e) {
    return date.toLocaleString(currentIntlTag(), { ...options, timeZone: DEFAULT_COMMUNITY_TIMEZONE })
  }
}

/** "5 minutes ago" / "yesterday" in the active language; a date after a week. */
export const relativeTime = (value, timeZone) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const seconds = Math.round((date.getTime() - Date.now()) / 1000)
  const abs = Math.abs(seconds)
  const locale = currentIntlTag()
  if (abs >= 7 * 86400) return formatInZone(date, { day: 'numeric', month: 'short' }, timeZone)
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  if (abs < 60) return format.format(0, 'second')
  if (abs < 3600) return format.format(Math.round(seconds / 60), 'minute')
  if (abs < 86400) return format.format(Math.round(seconds / 3600), 'hour')
  return format.format(Math.round(seconds / 86400), 'day')
}

export const clockTime = (value, timeZone) => formatInZone(value, { hour: 'numeric', minute: '2-digit' }, timeZone)

/** The admin's own timezone, from their profile; '' when not set. */
export const viewerTimezone = store => {
  const fromStore = store?.state?.user?.userData?.timezone
  if (fromStore) return fromStore
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}').timezone || ''
  } catch (e) {
    return ''
  }
}

/**
 * An activity's start in the admin's own timezone, for "(Tue 3 Nov, 11:25 PM
 * your time)". `startsAt` is the real moment and `startsIn` the timezone the
 * text already shows it in; '' when the admin's clock reads the same there.
 */
export const yourStartTime = (startsAt, startsIn, viewerTz) => {
  if (!startsAt || !startsIn || !viewerTz) return ''
  const options = { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }
  const yours = formatInZone(startsAt, options, viewerTz)
  return yours === formatInZone(startsAt, options, startsIn) ? '' : yours
}

/** Locked (trial ended) communities can only open Home and Billing. */
export const isCommunityLocked = () => {
  try {
    return JSON.parse(localStorage.getItem('userData'))?.isPremium === false
  } catch (e) {
    return false
  }
}
